/**
 * Push notifications — client side (see shared/src/push.ts for the model).
 *
 * The FCM token is minted against the APP's own service worker registration
 * (push handlers are imported into it from public/push-sw.js), so there is
 * only one worker. The token of this device is remembered in localStorage so
 * the Profile card can show on/off and the app can re-register a rotated token.
 *
 * iOS: Web Push works only in the installed home-screen app (iOS 16.4+), and
 * the permission prompt must come from a tap — hence the explicit button.
 */
import { httpsCallable } from 'firebase/functions';
import { CALLABLES, type PushRegisterRequest, type PushUnregisterRequest, type PushTestResponse } from '@daaily/shared';
import { app, functions } from '../lib/firebase';
import { isStandalone } from '../applock/appLock';

const TOKEN_KEY = 'daaily.push.token';
const VAPID_KEY = (import.meta.env.VITE_FCM_VAPID_KEY as string | undefined) ?? '';

const callRegister = httpsCallable<PushRegisterRequest, { ok: true }>(functions, CALLABLES.PUSH_REGISTER, { timeout: 30_000 });
const callUnregister = httpsCallable<PushUnregisterRequest, { ok: true }>(functions, CALLABLES.PUSH_UNREGISTER);
const callTest = httpsCallable<unknown, PushTestResponse>(functions, CALLABLES.PUSH_TEST);

export type PushAvailability =
  | 'ok'
  | 'unsupported' // browser has no Web Push
  | 'install-first' // iOS/iPadOS in a browser tab — must add to Home Screen
  | 'not-configured'; // no VAPID key in this build

function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

export function pushAvailability(): PushAvailability {
  if (!VAPID_KEY) return 'not-configured';
  if (isIos() && !isStandalone()) return 'install-first';
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return 'unsupported';
  }
  return 'ok';
}

function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // storage blocked — the device simply shows as off next time
  }
}

/** True when this device has push turned on (token stored + permission kept). */
export function isPushOnHere(): boolean {
  return !!readToken() && typeof Notification !== 'undefined' && Notification.permission === 'granted';
}

function deviceLabel(): string {
  const ua = navigator.userAgent;
  const os = /iPhone/.test(ua) ? 'iPhone' : /iPad/.test(ua) || isIos() ? 'iPad' : /Android/.test(ua) ? 'Android' : /Mac/.test(ua) ? 'Mac' : /Windows/.test(ua) ? 'Windows' : 'Device';
  return `${os}${isStandalone() ? ' · app' : ' · browser'}`;
}

/**
 * The steps of turning push on. Each one is time-boxed: a step that never
 * settles (Oct 2026: the Home button sat on "Turning on…" for good on an
 * iPhone) now ends in an error that names the step, instead of a dead button.
 */
export type PushStep = 'permission' | 'worker' | 'token' | 'register';

export const PUSH_STEP_LABEL: Record<PushStep, string> = {
  permission: 'Waiting for your answer…',
  worker: 'Getting the app ready…',
  token: 'Setting up this device…',
  register: 'Almost done…',
};

/** Rejects with `message` when `p` hasn't settled after `ms`. */
function withTimeout<T>(p: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), ms);
    p.then(
      (v) => { window.clearTimeout(timer); resolve(v); },
      (e) => { window.clearTimeout(timer); reject(e); },
    );
  });
}

/**
 * The app's own service worker, active. `navigator.serviceWorker.ready` never
 * settles while no worker is registered (one cleared by the error boundary's
 * recovery, or a failed install) and waits out a first install that is still
 * downloading the ~9 MB precache, so: register /sw.js if nothing is there, then
 * wait a bounded time for it to activate.
 */
async function activeRegistration(): Promise<ServiceWorkerRegistration> {
  const busy = 'The app is still finishing an update in the background. Keep it open for a minute, then try again. (push:worker)';
  const find = async () => {
    const existing = await navigator.serviceWorker.getRegistration('/');
    if (existing?.active) return existing;
    if (!existing) await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    return navigator.serviceWorker.ready;
  };
  try {
    return await withTimeout(find(), 45_000, busy);
  } catch (e) {
    if (e instanceof Error && e.message === busy) throw e;
    const detail = e instanceof Error && e.message ? `: ${e.message}` : '';
    throw new Error(`The app's background worker could not start${detail}. Close DAAily Hub completely, open it again and try once more. (push:worker)`);
  }
}

async function mintToken(onStep?: (step: PushStep) => void): Promise<string> {
  onStep?.('worker');
  const registration = await activeRegistration();
  onStep?.('token');
  const timedOut = 'Could not reach the notification service. Check your connection and try again. (push:token)';
  // The messaging chunk and isSupported() (it opens IndexedDB, which can stall
  // on iOS) share the token step's time box.
  const { getMessaging, getToken, isSupported } = await withTimeout(import('firebase/messaging'), 30_000, timedOut);
  if (!(await withTimeout(isSupported(), 30_000, timedOut))) {
    throw new Error('This browser does not support push notifications.');
  }
  const mint = () => withTimeout(
    getToken(getMessaging(app), { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration }),
    30_000,
    timedOut,
  );
  try {
    return await mint();
  } catch (e) {
    if (e instanceof Error && e.message === timedOut) throw e;
    // Safari's first push subscription sometimes fails with a transient
    // "push service error"; a second attempt usually goes through.
    await new Promise((r) => window.setTimeout(r, 1000));
    try {
      return await mint();
    } catch (e2) {
      if (e2 instanceof Error && e2.message === timedOut) throw e2;
      const detail = e2 instanceof Error && e2.message ? `: ${e2.message}` : '';
      throw new Error(`Could not set up notifications on this device${detail} (push:token)`);
    }
  }
}

/** Turn push on for this device. Must be called from a tap (iOS): the permission request runs first, before any await. */
export async function enablePush(onStep?: (step: PushStep) => void): Promise<void> {
  onStep?.('permission');
  const permission = await withTimeout(
    Notification.requestPermission(),
    60_000,
    'No answer to the permission prompt within a minute. Tap the button again; if no prompt shows, close DAAily Hub completely and open it again. (push:permission)',
  );
  if (permission === 'default') {
    throw new Error('You closed the prompt without choosing. Tap the button again and choose Allow.');
  }
  if (permission !== 'granted') {
    throw new Error(
      isIos()
        ? 'Notifications are blocked for DAAily Hub. Turn them on in Settings → Notifications → DAAily Hub, then try again.'
        : 'Notifications are blocked for DAAily Hub. Allow them in your browser or device settings, then try again.',
    );
  }
  const token = await mintToken(onStep);
  onStep?.('register');
  try {
    await callRegister({ token, device: deviceLabel() });
  } catch (e) {
    const detail = e instanceof Error && e.message ? ` (${e.message})` : '';
    throw new Error(`This device could not be saved to your account${detail}. Try again in a moment. (push:register)`);
  }
  writeToken(token);
}

/** Turn push off for this device. */
export async function disablePush(): Promise<void> {
  const token = readToken();
  writeToken(null);
  if (!token) return;
  try {
    const { getMessaging, deleteToken } = await import('firebase/messaging');
    await deleteToken(getMessaging(app));
  } catch {
    // best effort — the server copy is what matters
  }
  await callUnregister({ token });
}

export async function sendTestPush(): Promise<PushTestResponse> {
  const res = await callTest({});
  return res.data;
}

/**
 * On app start: if this device has push on, re-mint the token and re-register
 * when FCM rotated it. Silent; never throws.
 */
export async function refreshPushToken(): Promise<void> {
  const stored = readToken();
  if (!stored || pushAvailability() !== 'ok') return;
  if (Notification.permission !== 'granted') {
    // Permission revoked in OS settings — forget the device server-side.
    await disablePush().catch(() => undefined);
    return;
  }
  try {
    const token = await mintToken();
    if (token !== stored) {
      await callRegister({ token, device: deviceLabel() });
      await callUnregister({ token: stored }).catch(() => undefined);
      writeToken(token);
    }
  } catch {
    // offline / transient — try again next launch
  }
}
